// FILE: client/src/actions/categoryActions.js

import * as C from "../constants/categoryConstants";
import api from "../utils/axiosInstance";
import { getApiErrorMessage } from "../utils/errorMessages";

const getErrorMessage = (error) =>
  getApiErrorMessage(error, {
    fallback:
      "We could not update categories right now. Please try again later.",
  });

const normalizeString = (value = "") => String(value || "").trim();

const normalizePositiveInt = (value, fallback) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

const normalizeDisplayOrder = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 && n <= 9999 ? n : fallback;
};

const normalizeImages = (images) => {
  if (!Array.isArray(images)) return [];

  return images
    .filter((image) => image && typeof image === "object")
    .map((image) => ({
      url: normalizeString(image.url),
      public_id: normalizeString(image.public_id),
      alt: normalizeString(image.alt),
      caption: normalizeString(image.caption),
    }))
    .filter((image) => image.url);
};

const dispatchFailure = (dispatch, type, error) => {
  dispatch({
    type,
    payload: getErrorMessage(error),
  });
};

const buildCategoryListQuery = ({
  shopKey,
  keyword = "",
  pageNumber = 1,
  pageSize = 100,
  isPublished,
}) => {
  const params = new URLSearchParams();

  const safeShopKey = normalizeString(shopKey);
  const safeKeyword = normalizeString(keyword);
  const safePageNumber = normalizePositiveInt(pageNumber, 1);
  const safePageSize = normalizePositiveInt(pageSize, 100);

  if (safeShopKey) params.set("shopKey", safeShopKey);
  if (safeKeyword) params.set("keyword", safeKeyword);
  if (safePageNumber) params.set("pageNumber", String(safePageNumber));
  if (safePageSize) params.set("pageSize", String(safePageSize));

  if (typeof isPublished === "boolean") {
    params.set("isPublished", String(isPublished));
  }

  return params.toString();
};

/* ----------------------------- Reset / UI helpers ----------------------------- */

export const resetCategoryList = () => ({ type: C.CATEGORY_LIST_RESET });

export const resetCategoryDetails = () => ({
  type: C.CATEGORY_DETAILS_RESET,
});

export const resetAdminCategoryList = () => ({
  type: C.ADMIN_CATEGORY_LIST_RESET,
});

export const resetAdminCategoryDetails = () => ({
  type: C.ADMIN_CATEGORY_DETAILS_RESET,
});

export const resetAdminCategoryCreate = () => ({
  type: C.ADMIN_CATEGORY_CREATE_RESET,
});

export const resetAdminCategoryUpdate = () => ({
  type: C.ADMIN_CATEGORY_UPDATE_RESET,
});

export const resetAdminCategoryDelete = () => ({
  type: C.ADMIN_CATEGORY_DELETE_RESET,
});

export const clearCategoryError = () => ({
  type: C.CATEGORY_CLEAR_ERROR,
});

export const clearAdminCategoryError = () => ({
  type: C.ADMIN_CATEGORY_CLEAR_ERROR,
});

/* ----------------------------- Public ----------------------------- */

export const listCategories =
  ({ shopKey, keyword = "", pageNumber = 1, pageSize = 100 } = {}) =>
  async (dispatch) => {
    const queryParams = {
      shopKey: normalizeString(shopKey),
      keyword: normalizeString(keyword),
      pageNumber: normalizePositiveInt(pageNumber, 1),
      pageSize: normalizePositiveInt(pageSize, 100),
    };

    if (!queryParams.shopKey) {
      return dispatch({
        type: C.CATEGORY_LIST_FAIL,
        payload: "shopKey is required.",
      });
    }

    try {
      dispatch({
        type: C.CATEGORY_LIST_REQUEST,
        meta: queryParams,
      });

      const query = buildCategoryListQuery(queryParams);

      const { data } = await api.get(`/categories?${query}`);

      dispatch({
        type: C.CATEGORY_LIST_SUCCESS,
        payload: data,
        meta: queryParams,
      });
    } catch (error) {
      dispatchFailure(dispatch, C.CATEGORY_LIST_FAIL, error);
    }
  };

export const detailsCategory = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  if (!safeId) {
    return dispatch({
      type: C.CATEGORY_DETAILS_FAIL,
      payload: "Category id is required.",
    });
  }

  try {
    dispatch({
      type: C.CATEGORY_DETAILS_REQUEST,
      meta: { id: safeId },
    });

    const { data } = await api.get(`/categories/${encodeURIComponent(safeId)}`);

    dispatch({
      type: C.CATEGORY_DETAILS_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });
  } catch (error) {
    dispatchFailure(dispatch, C.CATEGORY_DETAILS_FAIL, error);
  }
};

/* ----------------------------- Admin ----------------------------- */

export const adminListCategories =
  ({
    shopKey,
    keyword = "",
    pageNumber = 1,
    pageSize = 100,
    isPublished,
  } = {}) =>
  async (dispatch) => {
    const queryParams = {
      shopKey: normalizeString(shopKey),
      keyword: normalizeString(keyword),
      pageNumber: normalizePositiveInt(pageNumber, 1),
      pageSize: normalizePositiveInt(pageSize, 100),
      isPublished,
    };

    if (!queryParams.shopKey) {
      const errorPayload = "shopKey is required.";

      dispatch({
        type: C.ADMIN_CATEGORY_LIST_FAIL,
        payload: errorPayload,
      });

      throw new Error(errorPayload);
    }

    try {
      dispatch({
        type: C.ADMIN_CATEGORY_LIST_REQUEST,
        meta: queryParams,
      });

      const query = buildCategoryListQuery(queryParams);

      const { data } = await api.get(`/categories/admin/list?${query}`);

      dispatch({
        type: C.ADMIN_CATEGORY_LIST_SUCCESS,
        payload: data,
        meta: queryParams,
      });

      return data;
    } catch (error) {
      dispatchFailure(dispatch, C.ADMIN_CATEGORY_LIST_FAIL, error);
      throw error;
    }
  };

export const adminGetCategory = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  if (!safeId) {
    return dispatch({
      type: C.ADMIN_CATEGORY_DETAILS_FAIL,
      payload: "Category id is required.",
    });
  }

  try {
    dispatch({
      type: C.ADMIN_CATEGORY_DETAILS_REQUEST,
      meta: { id: safeId },
    });

    const { data } = await api.get(
      `/categories/admin/${encodeURIComponent(safeId)}`,
    );

    dispatch({
      type: C.ADMIN_CATEGORY_DETAILS_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });

    return data;
  } catch (error) {
    dispatchFailure(dispatch, C.ADMIN_CATEGORY_DETAILS_FAIL, error);
    throw error;
  }
};

export const adminCreateCategory = (category) => async (dispatch) => {
  const payload = {
    shopKey: normalizeString(category?.shopKey),
    name: normalizeString(category?.name),
    slug: normalizeString(category?.slug),
    description: category?.description || "",
    images: normalizeImages(category?.images),
    displayOrder: normalizeDisplayOrder(category?.displayOrder),
    isPublished:
      typeof category?.isPublished === "boolean" ? category.isPublished : true,
  };

  if (!payload.shopKey || !payload.name) {
    const errorPayload = "shopKey and name are required.";

    dispatch({
      type: C.ADMIN_CATEGORY_CREATE_FAIL,
      payload: errorPayload,
    });

    throw new Error(errorPayload);
  }

  try {
    dispatch({ type: C.ADMIN_CATEGORY_CREATE_REQUEST });

    const { data } = await api.post("/categories/admin", payload);

    dispatch({
      type: C.ADMIN_CATEGORY_CREATE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    dispatchFailure(dispatch, C.ADMIN_CATEGORY_CREATE_FAIL, error);
    throw error;
  }
};

export const adminUpdateCategory = (id, category) => async (dispatch) => {
  const safeId = normalizeString(id);

  if (!safeId) {
    return dispatch({
      type: C.ADMIN_CATEGORY_UPDATE_FAIL,
      payload: "Category id is required.",
    });
  }

  const payload = {};
  const source = category || {};

  if ("name" in source) {
    payload.name = normalizeString(source.name);
  }

  if ("slug" in source) {
    payload.slug = normalizeString(source.slug);
  }

  if ("description" in source) {
    payload.description = source.description || "";
  }

  if ("images" in source) {
    payload.images = normalizeImages(source.images);
  }

  if ("displayOrder" in source) {
    payload.displayOrder = normalizeDisplayOrder(source.displayOrder);
  }

  if ("isPublished" in source) {
    payload.isPublished =
      typeof source.isPublished === "boolean"
        ? source.isPublished
        : String(source.isPublished).toLowerCase() === "true";
  }

  try {
    dispatch({
      type: C.ADMIN_CATEGORY_UPDATE_REQUEST,
      meta: { id: safeId },
    });

    const { data } = await api.put(
      `/categories/admin/${encodeURIComponent(safeId)}`,
      payload,
    );

    dispatch({
      type: C.ADMIN_CATEGORY_UPDATE_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });

    return data;
  } catch (error) {
    dispatchFailure(dispatch, C.ADMIN_CATEGORY_UPDATE_FAIL, error);
    throw error;
  }
};

export const adminDeleteCategory = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  if (!safeId) {
    return dispatch({
      type: C.ADMIN_CATEGORY_DELETE_FAIL,
      payload: "Category id is required.",
    });
  }

  try {
    dispatch({
      type: C.ADMIN_CATEGORY_DELETE_REQUEST,
      meta: { id: safeId },
    });

    await api.delete(`/categories/admin/${encodeURIComponent(safeId)}`);

    dispatch({
      type: C.ADMIN_CATEGORY_DELETE_SUCCESS,
      payload: { id: safeId },
      meta: { id: safeId },
    });

    return { id: safeId };
  } catch (error) {
    dispatchFailure(dispatch, C.ADMIN_CATEGORY_DELETE_FAIL, error);
    throw error;
  }
};
